/**
 * 首页通用动画效果库
 * 使用 Intersection Observer API 监听元素进入视口
 */

class UGAnimationController {
    constructor() {
      this.init();
    }
  
    /**
     * 初始化所有动画效果
     */
    init() {
      this.initOpacityAnimation();
      this.initContainerAnimation();
      this.initNumberCountAnimation();
      this.initScrollScaleAnimation();
      // 在此处添加更多动画效果...
    }
  
    /**
     * 创建 Intersection Observer
     * @param {Function} callback - 元素进入视口时的回调函数
     * @param {Object} options - Observer 配置选项
     * @returns {IntersectionObserver}
     */
    createObserver(callback, options = {}) {
      const defaultOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1, // 元素 10% 可见时触发
        ...options
      };
  
      return new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            callback(entry.target, observer);
          }
        });
      }, defaultOptions);
    }
  
    /**
     * 效果 1: 缓动出现动画
     * 监听 [data-animated="opacity"] 元素，添加 ug-animated-opacity 类名
     */
    initOpacityAnimation() {
      const opacityElements = document.querySelectorAll('[data-animated="opacity"]');
      
      if (opacityElements.length === 0) return;
  
      const observer = this.createObserver((/** @type {Element} */ element, /** @type {IntersectionObserver} */ observer) => {
        element.classList.add('ug-animated-opacity');
        // 动画完成后停止观察该元素
        observer.unobserve(element);
      });
  
      opacityElements.forEach(element => observer.observe(element));
    }
  
    /**
     * 效果 2: 容器项动画
     * 监听 [data-animated="container"] 元素，
     * 给子元素 [data-animated="container-item"] 添加 ug-animated-container-item 类名
     * 使用 CSS transition-delay 实现层叠效果，动画完成后自动清理
     */
    initContainerAnimation() {
      const containerElements = document.querySelectorAll('[data-animated="container"]');
      
      if (containerElements.length === 0) return;
  
      const observer = this.createObserver((/** @type {Element} */ container, /** @type {IntersectionObserver} */ observer) => {
        // 先给容器添加动画类
        container.classList.add('ug-animated-container');
        
        const items = container.querySelectorAll('[data-animated="container-item"]');
        // 未配置时保持原有 150ms 节奏；仅显式传入的容器使用自定义值。
        const configuredDelayStep = Number.parseFloat(container.dataset.animatedDelayStep);
        const configuredDelayMax = Number.parseFloat(container.dataset.animatedDelayMax);
        const delayStep = Number.isFinite(configuredDelayStep) && configuredDelayStep >= 0
          ? configuredDelayStep
          : 150;
        const delayMax = Number.isFinite(configuredDelayMax) && configuredDelayMax >= 0
          ? configuredDelayMax
          : Number.POSITIVE_INFINITY;
        
        // 为每个子项设置 transition-delay 并添加动画类
        items.forEach((/** @type {HTMLElement} */ item, /** @type {number} */ index) => {
          const delay = Math.min(index * delayStep, delayMax);
          
          // 设置 transition-delay（CSS 会自动应用过渡效果）
          item.style.transitionDelay = `${delay}ms`;
          
          // 监听过渡结束事件，清理 transition-delay
          const handleTransitionEnd = (/** @type {TransitionEvent} */ e) => {
            // 只处理当前元素的过渡事件，避免处理子元素的事件
            if (e.target === item && e.propertyName === 'opacity') {
              // 清除内联的 transition-delay
              item.style.transitionDelay = '';
              // 移除事件监听器
              item.removeEventListener('transitionend', handleTransitionEnd);
            }
          };
          
          item.addEventListener('transitionend', handleTransitionEnd);
          
          // 添加动画类名（使用 requestAnimationFrame 确保在下一帧执行，保证过渡生效）
          requestAnimationFrame(() => {
            item.classList.add('ug-animated-container-item');
          });
        });
  
        // 动画完成后停止观察该容器
        observer.unobserve(container);
      });
  
      containerElements.forEach(container => observer.observe(container));
    }
  
  
    /**
     * 效果 3: 数字滚动动画
     * 监听 [data-animated="number"] 元素，从 0 滚动到目标数字
     * 支持带单位的数字，如 "70+"、"200M+"、"1.5K" 等
     */
    initNumberCountAnimation() {
      const numberElements = document.querySelectorAll('[data-animated="number"]');
      
      if (numberElements.length === 0) return;
  
      const observer = this.createObserver((/** @type {HTMLElement} */ element, /** @type {IntersectionObserver} */ observer) => {
        const durationAttr = element.getAttribute('data-animated-duration');
        const durationNumber = Number(durationAttr);
        const duration = Number.isFinite(durationNumber) && durationNumber > 0 ? durationNumber : 2000;
        this.animateNumber(element, duration);
        // 动画完成后停止观察该元素
        observer.unobserve(element);
      }, { threshold: 0.3 }); // 元素 30% 可见时触发
  
      numberElements.forEach(element => observer.observe(element));
    }
  
    /**
     * 执行数字滚动动画
     * @param {HTMLElement} element - 要动画的元素
     * @param {number} duration - 动画时长
     */
    animateNumber(element,duration = 2000) {
      const originalText = element.textContent || '0';
      
      // 解析数字和单位（支持 "70+"、"200M+"、"1,234" 等格式）
      const parseResult = this.parseNumberWithUnit(originalText);
      if (!parseResult) return;
  
      const { number: targetNumber, prefix, suffix } = parseResult;
      
      // 确保目标数字是正数
      if (targetNumber < 0) {
        console.warn('数字滚动动画：目标数字不能为负数', originalText, targetNumber);
        return;
      }
      
      // 动画参数
      const startNumber = 0;
      /** @type {number|null} */
      let startTime = null;
  
      // 使用 easeOutCubic 缓动函数
      const easeOutCubic = (/** @type {number} */ t) => {
        return 1 - Math.pow(1 - t, 3);
      };
  
      // 动画循环
      const animate = (/** @type {number} */ currentTime) => {
        // 记录开始时间
        if (startTime === null) {
          startTime = currentTime;
          // 立即设置初始值为 0
          element.textContent = prefix + '0' + suffix;
        }
        
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        
        // 计算当前数字（确保不会出现负数）
        const currentNumber = Math.max(0, startNumber + (targetNumber - startNumber) * easedProgress);
        
        // 格式化并显示数字
        element.textContent = prefix + this.formatNumber(currentNumber, targetNumber) + suffix;
        
        // 继续动画或结束
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // 确保最终显示原始文本
          element.textContent = originalText;
        }
      };
  
      requestAnimationFrame(animate);
    }
  
    /**
     * 解析带单位的数字字符串
     * @param {string} text - 原始文本
     * @returns {{number: number, prefix: string, suffix: string}|null} - { number: 数字, prefix: 前缀, suffix: 后缀 }
     */
    parseNumberWithUnit(text) {
      // 移除空格并转换为字符串
      const cleanText = String(text).trim();
      
      // 匹配模式：可选前缀 + 数字（可含小数点和逗号） + 可选后缀
      const match = cleanText.match(/^([^\d]*)?([\d,\.]+)(.*)$/);
      
      if (!match) return null;
      
      const prefix = match[1] || '';
      const numberStr = match[2] || '0';
      const suffix = match[3] || '';
      
      // 移除逗号并转换为数字
      const number = parseFloat(numberStr.replace(/,/g, ''));
      
      if (isNaN(number)) return null;
      
      return { number, prefix, suffix };
    }
  
    /**
     * 格式化数字显示
     * @param {number} current - 当前数字
     * @param {number} target - 目标数字
     * @returns {string} - 格式化后的数字字符串
     */
    formatNumber(current, target) {
      // 判断目标数字是否有小数
      const hasDecimal = target % 1 !== 0;
      
      if (hasDecimal) {
        // 保留与目标数字相同的小数位数
        const decimalPlaces = target.toString().split('.')[1]?.length || 1;
        return current.toFixed(decimalPlaces);
      } else {
        // 整数直接取整
        return Math.floor(current).toString();
      }
    }
  
    /**
     * 效果 4: 滚动缩放动画
     * 监听 [data-animated="animated-on-scroll"] 元素
     * 根据元素在视口中的位置动态调整 scale
     * 从视口 10% 位置的 scale(0.8) 到 80% 位置的 scale(1.0)
     * 默认为放大，缩小  可以添加其他参数数据  data-animated-type="opacity,scale"【用,隔开需要执行的动画】   data-animated-initval="0 1,1 0" 【用，隔开需要执行的动画的初始值】 
     */
    initScrollScaleAnimation() {
      const scaleElements = document.querySelectorAll('[data-animated="animated-on-scroll"]');
      
      if (scaleElements.length === 0) return;
  
      // 使用 Map 存储每个元素的状态
      const elementStates = new Map();
  
      // 创建 Intersection Observer 来优化性能
      // 只有元素在视口附近时才进行滚动计算
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const element = /** @type {HTMLElement} */ (entry.target);
          
          if (entry.isIntersecting) {
            // 元素进入视口附近，开始监听滚动
            elementStates.set(element, { active: true });
            this.updateScaleOnScroll(element);
          } else {
            // 元素离开视口，停止更新（但保持状态）
            elementStates.set(element, { active: false });
          }
        });
      }, {
        root: null,
        rootMargin: '20% 0px', // 提前 20% 开始监听
        threshold: 0
      });
  
      // 观察所有目标元素
      scaleElements.forEach(element => {
        observer.observe(element);
        elementStates.set(element, { active: false });
      });
  
      // 滚动事件处理（使用节流优化性能）
      /** @type {number|null} */
      let rafId = null;
      const handleScroll = () => {
        if (rafId) return;
        
        rafId = requestAnimationFrame(() => {
          elementStates.forEach((state, element) => {
            if (state.active) {
              this.updateScaleOnScroll(/** @type {HTMLElement} */ (element));
            }
          });
          rafId = null;
        });
      };
  
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // 初始化时计算一次
      scaleElements.forEach(element => {
        this.updateScaleOnScroll(/** @type {HTMLElement} */ (element));
      });
    }
  
    /**
     * 根据元素在视口中的位置更新 scale
     * @param {HTMLElement} element - 要更新的元素
     */
    updateScaleOnScroll(element) {
      // 获取元素和视口信息
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // 计算元素中心点在视口中的位置（0 = 顶部，1 = 底部）
      const elementCenter = rect.top;
      const viewportPosition = elementCenter / windowHeight;
      
      // 定义缩放范围
      const startPosition = 0.9; // 元素中心在视口 90% 位置时开始（元素顶部约 10%）
      const endPosition = 0.2;   // 元素中心在视口 20% 位置时结束（元素顶部约 80%）

      const type = element.getAttribute('data-animated-type') ? element.getAttribute('data-animated-type').split(',').map(t => t.trim()) : ['scale'];
      const initval = element.getAttribute('data-animated-initval') ? element.getAttribute('data-animated-initval').split(',').map(v => v.trim()) : ['0.8 1'];

      // 计算进度值（0 到 1）
      let progress;
      if (viewportPosition >= startPosition) {
        // 元素还在下方，保持最小值
        progress = 0;
      } else if (viewportPosition <= endPosition) {
        // 元素已经到达目标位置，保持最大值
        progress = 1;
      } else {
        // 元素在过渡区间，计算线性插值
        progress = (startPosition - viewportPosition) / (startPosition - endPosition);
      }

      // 存储各种动画值
      const transformProps = {
        scale: null,
        translateX: null,
        translateY: null,
        rotate: null,
        // 可以扩展更多 transform 属性
      };
      let opacity = null;

      // 遍历每个动画类型，计算对应的值
      type.forEach((animType, index) => {
        const trimmedType = animType.trim();
        // 获取对应的初始值范围，如果没有则使用默认值
        const valRange = initval[index] || (trimmedType === 'scale' ? '0.8 1' : '0 1');
        const [minVal, maxVal] = valRange.split(' ').map(v => parseFloat(v.trim()));

        // 根据进度计算当前值
        const currentValue = minVal + (maxVal - minVal) * progress;

        // 根据动画类型应用值
        switch (trimmedType) {
          case 'scale':
            transformProps.scale = currentValue;
            break;
          case 'translateX':
            transformProps.translateX = currentValue;
            break;
          case 'translateY':
            transformProps.translateY = currentValue;
            break;
          case 'rotate':
            transformProps.rotate = currentValue;
            break;
          case 'opacity':
            opacity = currentValue;
            break;
          // 可以在这里添加更多动画类型
          default:
            console.warn(`未知的动画类型: ${trimmedType}`);
        }
      });

      // 合并所有 transform 值
      const transformParts = [];
      if (transformProps.translateX !== null) {
        transformParts.push(`translateX(${transformProps.translateX}px)`);
      }
      if (transformProps.translateY !== null) {
        transformParts.push(`translateY(${transformProps.translateY}px)`);
      }
      if (transformProps.rotate !== null) {
        transformParts.push(`rotate(${transformProps.rotate}deg)`);
      }
      if (transformProps.scale !== null) {
        transformParts.push(`scale(${transformProps.scale})`);
      }

      // 应用变换
      if (transformParts.length > 0) {
        element.style.transform = transformParts.join(' ');
      }
      
      // 应用透明度
      if (opacity !== null) {
        element.style.opacity = opacity.toString();
      }
    }
  
    /**
     * 效果 5: 示例 - 淡入动画
     * 可以在此添加更多动画效果
     */
    // initFadeInAnimation() {
    //   const elements = document.querySelectorAll('[data-animated="fade-in"]');
    //   
    //   if (elements.length === 0) return;
    //
    //   const observer = this.createObserver((element, observer) => {
    //     element.classList.add('ug-animated-fade-in');
    //     observer.unobserve(element);
    //   });
    //
    //   elements.forEach(element => observer.observe(element));
    // }
  }
  
  // DOM 加载完成后初始化动画控制器（挂到 window，供 swiper 等模块在切 slide 时手动触发数字滚动）
  document.addEventListener('DOMContentLoaded', () => {
    window.ugAnimationController = new UGAnimationController();
  });
